import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gantt, Tooltip, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/all.css';
import { format as formatDate, parseISO, startOfDay } from 'date-fns';
import { taskService } from '../../../services/quantifiedTaskService';
import { getScheduleTasks, type ScheduleTaskForChart } from '../../../services/scheduleService';
import type { IApi } from '@svar-ui/react-gantt';
import {
  parseDateOnly,
  resolveDragEndRange,
  type PlannedDatesMap,
  type SvarTask,
} from '../utils/scheduleGanttMapping';
import { buildSvarDependencyLinks } from '../utils/scheduleDependencyLinks';
import { tasksToSvarTasks } from '../utils/scheduleTaskGanttMapping';
import {
  buildPrintDependencyArrowInitScript,
  escapeHtml,
  formatDependencyNotes,
  stripScheduleIconPrefix,
} from '../utils/schedulePrint';
import { ScheduleTaskTooltipContent } from './ScheduleTaskTooltip';
import {
  normalizeScheduleFilterValues,
  taskMatchesScheduleActivityFilter,
  toScheduleListParam,
} from '../utils/scheduleResourceAggregation';
import { taskMatchesSchedulePlannedDateFilters } from '../utils/schedulePlannedDateWindow';
import { computeScheduleGanttTimeline } from '../utils/scheduleGanttTimeline';
import {
  computeTodayMarkerOffsetInScalePx,
  SCHEDULE_GANTT_TIMELINE_CELL_WIDTH,
} from '../utils/scheduleGanttTodayLineLayout';
import {
  getScheduleGanttHorizontalScrollTarget,
  useScheduleGanttChartScroll,
} from '../hooks/useScheduleGanttChartScroll';
import { taskStatusMatchesScheduleSelection } from '../utils/scheduleTaskStatuses';
import type { ScheduleTimelineScale } from './ScheduleTimelineScaleSwitcher';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { ID_LINK_CLASS } from '@/components/ui';
import { SCHEDULE_COMPACT_GRID_QUERY, scheduleTaskGanttColumns } from '../utils/scheduleGanttColumns';
import { Alert } from '@/components/ui';

interface TaskGanttChartProps {
  /** Structural subset of `ScheduleResourcePageFilters`; every dropdown field is a multiselect. */
  filters: {
    serviceAreas: string[];
    foremanAreas: string[];
    equipmentTypes: string[];
    projectIds: number[];
    activityIds: number[];
    activityNumbers: string[];
    statuses: string[];
    startDate: string;
    endDate: string;
    workTypes: string[];
    taskSearch: string;
  };
  scalePreset: ScheduleTimelineScale;
}

function hasPlannedStartAndEnd(task: ScheduleTaskForChart): boolean {
  if (task.yearRoundSchedule) return true;
  const plannedStartRaw = String(task.identifiedDate ?? task.startDate ?? '').trim();
  const plannedEndRaw = String(task.plannedCompletionDate ?? '').trim();
  return plannedStartRaw.length > 0 && plannedEndRaw.length > 0;
}

function updateTaskDatesInScheduleTasks(
  data: ScheduleTaskForChart[] | null,
  taskId: number,
  identifiedDate: string,
  plannedCompletionDate: string
): ScheduleTaskForChart[] | null {
  if (!data || !Array.isArray(data)) return data;
  return data.map((t) =>
    t.id === taskId ? { ...t, identifiedDate, plannedCompletionDate } : t
  );
}

const TaskGanttChart: React.FC<TaskGanttChartProps> = ({ filters, scalePreset }) => {
  const openTaskInNewTab = useCallback(
    (taskId: number) => {
      const taskUrl = `/tasks/quantified?taskId=${taskId}`;
      window.open(taskUrl, '_blank', 'noopener,noreferrer');
    },
    []
  );
  const [loading, setLoading] = useState(false);
  const [scheduleTasks, setScheduleTasks] = useState<ScheduleTaskForChart[] | null>(null);
  const [ganttApi, setGanttApi] = useState<unknown>(null);
  // Print is started from the Print schedule dialog, which closes first – so a failure shows here, on the chart.
  const [printError, setPrintError] = useState<string | null>(null);
  const initGantt = useCallback((api: unknown) => setGanttApi(api), []);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const bottomHScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomHScrollInnerRef = useRef<HTMLDivElement | null>(null);
  const tasksRef = useRef<SvarTask[]>([]);
  const scheduleTasksRef = useRef<ScheduleTaskForChart[] | null>(null);

  const { dateFrom, dateTo, scalesConfig } = useMemo(
    () => computeScheduleGanttTimeline(filters.startDate, filters.endDate, scalePreset),
    [scalePreset, filters.startDate, filters.endDate]
  );

  /**
   * Multiselect area/work-type filters in wire form (comma-separated, `undefined` when empty).
   * Strings, so they are stable effect/callback deps regardless of array identity.
   */
  const serviceAreaParam = toScheduleListParam(filters.serviceAreas);
  const foremanAreaParam = toScheduleListParam(filters.foremanAreas);
  const workTypeParam = toScheduleListParam(filters.workTypes);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getScheduleTasks({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      taskSearch: filters.taskSearch || undefined,
      statuses: filters.statuses || undefined,
    })
      .then((data) => {
        if (!cancelled) setScheduleTasks(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('Failed to load schedule tasks:', err);
          setScheduleTasks(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, serviceAreaParam, foremanAreaParam, workTypeParam, filters.taskSearch, filters.statuses]);

  const rangeStart = useMemo(() => parseDateOnly(dateFrom) ?? parseISO(dateFrom), [dateFrom]);
  const rangeEnd = useMemo(() => parseDateOnly(dateTo) ?? parseISO(dateTo), [dateTo]);

  const reloadScheduleTasks = useCallback(() => {
    return getScheduleTasks({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      taskSearch: filters.taskSearch || undefined,
      statuses: filters.statuses || undefined,
    }).then((data) => setScheduleTasks(Array.isArray(data) ? data : []));
  }, [dateFrom, dateTo, serviceAreaParam, foremanAreaParam, workTypeParam, filters.taskSearch, filters.statuses]);

  const safeTasks = useMemo(() => {
    const projectIds = (filters.projectIds || []).filter((id) => Number.isFinite(id) && id > 0);
    const statuses = (filters.statuses || []).map((s) => String(s || '').trim()).filter((s) => s.length > 0);
    const source = (Array.isArray(scheduleTasks) ? scheduleTasks : []).filter((task) => {
      if (!hasPlannedStartAndEnd(task)) return false;

      if (projectIds.length > 0) {
        const pid = task.project?.id || 0;
        if (!projectIds.includes(pid)) return false;
      }

      if (
        !taskMatchesScheduleActivityFilter(task, {
          activityIds: filters.activityIds || [],
          activityNumbers: filters.activityNumbers || [],
        })
      ) {
        return false;
      }

      if (!taskMatchesSchedulePlannedDateFilters(task, filters.startDate, filters.endDate)) {
        return false;
      }

      if (statuses.length > 0) {
        const st = String(task.status ?? '').trim();
        if (!taskStatusMatchesScheduleSelection(st, statuses)) return false;
      }

      // Equipment type is a multiselect: keep the task when any assigned unit matches any of the
      // selected types (same substring match on unit number this view has always used).
      const equipmentNeedles = normalizeScheduleFilterValues(filters.equipmentTypes).map((t) =>
        t.toLowerCase()
      );
      if (equipmentNeedles.length > 0) {
        const vehicles = task.vehicles || [];
        const unitHit = vehicles.some((v) => {
          const unit = (v.unitUsed || '').toLowerCase();
          return equipmentNeedles.some((needle) => unit.includes(needle));
        });
        if (!unitHit) return false;
      }
      return true;
    });
    const rows = tasksToSvarTasks(source, rangeStart, rangeEnd);
    return rows.filter(
      (row): row is SvarTask =>
        row != null &&
        typeof row.id === 'number' &&
        row.start instanceof Date &&
        row.end instanceof Date
    );
  }, [
    scheduleTasks,
    rangeStart,
    rangeEnd,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.startDate,
    filters.endDate,
    filters.equipmentTypes,
    filters.statuses,
  ]);

  tasksRef.current = safeTasks;
  scheduleTasksRef.current = scheduleTasks;

  const UPDATE_TASK_TAG = 'schedule-task-persist-dates';
  useEffect(() => {
    const api = ganttApi as {
      on?: (event: string, handler: (ev: { id: number | string; task?: { start?: Date | string; end?: Date | string }; inProgress?: boolean }) => void, options?: { tag?: string }) => void;
      detach?: (tag: string) => void;
    } | null;
    if (!api?.on) return;

    const handleUpdateTask = (ev: {
      id: number | string;
      task?: { start?: Date | string; end?: Date | string };
      inProgress?: boolean;
    }) => {
      const resolved = resolveDragEndRange(ev, ganttApi as IApi | null | undefined);
      if (!resolved) return;
      const { startDate, endDate } = resolved;
      const syntheticId = typeof ev.id === 'string' ? parseInt(ev.id, 10) : ev.id;
      if (Number.isNaN(syntheticId)) return;
      const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
      if (!svarTask?.sourceTaskId) return;

      const identifiedDateStr = formatDate(startDate, 'yyyy-MM-dd');
      const plannedCompletionDateStr = formatDate(endDate, 'yyyy-MM-dd');
      const currentData = scheduleTasksRef.current;
      const optimistic = updateTaskDatesInScheduleTasks(
        currentData,
        svarTask.sourceTaskId,
        identifiedDateStr,
        plannedCompletionDateStr
      );
      if (optimistic) setScheduleTasks(optimistic);

      taskService
        .updateTask(svarTask.sourceTaskId, { identifiedDate: startDate, plannedCompletionDate: endDate })
        .then(() => reloadScheduleTasks())
        .catch((err: unknown) => {
          console.error('Failed to persist task dates from task schedule Gantt:', err);
          reloadScheduleTasks().catch((reloadErr: unknown) => {
            console.error('Failed to reload task schedule after update failure:', reloadErr);
          });
        });
    };

    api.on('update-task', handleUpdateTask, { tag: UPDATE_TASK_TAG });
    return () => {
      if (api?.detach) api.detach(UPDATE_TASK_TAG);
    };
  }, [ganttApi, reloadScheduleTasks]);

  const safeLinks = useMemo(() => buildSvarDependencyLinks(safeTasks), [safeTasks]);

  // Portrait tablet (iPad 820x1180): five grid columns fill the width and push the timeline off
  // screen, so the grid collapses to Task Name only (W-iP3). Task ID, dates and status are still
  // in the bar tooltip.
  const compactGrid = useMediaQuery(SCHEDULE_COMPACT_GRID_QUERY);
  const columns = useMemo(() => scheduleTaskGanttColumns(compactGrid), [compactGrid]);

  const taskTypes = useMemo(
    () => [
      { id: 'task', label: 'Task' },
      { id: 'summary', label: 'Task' },
      { id: 'summary-empty', label: 'Project' },
      { id: 'planned', label: 'Planned' },
      { id: 'active', label: 'Active' },
      { id: 'closed', label: 'Closed / Finalized' },
      { id: 'overdue', label: 'Overdue' },
      { id: 'ongoing', label: 'Year-round / as needed' },
    ],
    []
  );

  const plannedDatesMap = useMemo(() => {
    const m: PlannedDatesMap = new Map();
    for (const t of safeTasks) {
      const hasPlannedFields = t.plannedStartDate !== undefined || t.plannedCompletionDate !== undefined;
      const hasAccomplishment = t.identifiedAccomplishment != null;
      if (!hasPlannedFields && !t.yearRoundSchedule && !hasAccomplishment) continue;
      m.set(t.id, {
        plannedStartDate: t.plannedStartDate ?? null,
        plannedCompletionDate: t.plannedCompletionDate ?? null,
        identifiedAccomplishment:
          t.identifiedAccomplishment != null && Number.isFinite(Number(t.identifiedAccomplishment))
            ? Number(t.identifiedAccomplishment)
            : null,
        dependencies: t.dependencies ?? null,
        yearRoundSchedule: t.yearRoundSchedule === true,
      });
    }
    return m;
  }, [safeTasks]);

  const assigneesByGanttId = useMemo(() => {
    const m = new Map<number | string, string>();
    const list = scheduleTasks ?? [];
    for (const g of safeTasks) {
      if (g.sourceTaskId == null) continue;
      const api = list.find((t) => t.id === g.sourceTaskId);
      if (!api?.assignees?.length) continue;
      const label = api.assignees
        .map((a) => `${a.name} ${a.lastName}`.trim())
        .filter((s) => s.length > 0)
        .join(', ');
      if (label) m.set(g.id, label);
    }
    return m;
  }, [safeTasks, scheduleTasks]);

  const tooltipContent = useCallback(
    (props: {
      data?: { id?: number | string; text?: string; start?: Date | string; end?: Date | string; progress?: number };
      api?: unknown;
    }) => (
      <ScheduleTaskTooltipContent
        {...props}
        plannedDatesMap={plannedDatesMap}
        assigneesByGanttId={assigneesByGanttId}
      />
    ),
    [plannedDatesMap, assigneesByGanttId]
  );

  const todayMarker = useMemo(
    () => [
      {
        start: startOfDay(new Date()),
        text: 'Today',
        css: 'schedule-today-marker',
      },
    ],
    []
  );

  const handlePrintSchedulePdf = useCallback(() => {
    setPrintError(null);
    try {
      const allTasks = Array.isArray(safeTasks) ? safeTasks : [];
      const topLevelIds = new Set(
        allTasks.filter((t) => t.parent === 0 && t.sourceTaskId == null).map((t) => t.id)
      );
      const childTasks = allTasks
        .filter((t) => t.sourceTaskId != null && t.parent != null && topLevelIds.has(t.parent))
        .slice()
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      const apiTaskById = new Map<number, ScheduleTaskForChart>();
      (Array.isArray(scheduleTasks) ? scheduleTasks : []).forEach((task) => {
        apiTaskById.set(task.id, task);
      });

      const sourceTaskIdToLabel = new Map<number, string>();
      allTasks.forEach((t) => {
        if (t.sourceTaskId != null) {
          sourceTaskIdToLabel.set(t.sourceTaskId, stripScheduleIconPrefix(t.text));
        }
      });

      const printableIds = new Set(childTasks.map((t) => t.id));
      const sourceTaskIdToNodeId = new Map<number, number>();
      childTasks.forEach((t) => {
        if (t.sourceTaskId != null) sourceTaskIdToNodeId.set(t.sourceTaskId, t.id);
      });
      const printLinks: Array<{ source: number; target: number }> = [];
      childTasks.forEach((t) => {
        const completionId = t.dependencies?.completionTaskId;
        if (completionId == null || t.sourceTaskId == null) return;
        const srcNode = sourceTaskIdToNodeId.get(Number(completionId));
        if (srcNode == null || srcNode === t.id || !printableIds.has(srcNode)) return;
        printLinks.push({ source: srcNode, target: t.id });
      });

      const rangeStartMs = startOfDay(rangeStart).getTime();
      const rangeEndMs = startOfDay(rangeEnd).getTime();
      const totalMs = Math.max(24 * 60 * 60 * 1000, rangeEndMs - rangeStartMs);

      const barColor = (type: string): string => {
        if (type === 'overdue') return '#333333';
        if (type === 'active') return '#58a55c';
        if (type === 'closed') return '#6c757d';
        if (type === 'ongoing') return '#6f42c1';
        return '#86b8e7';
      };

      const toDate = (val?: string | null): Date | null => {
        if (!val) return null;
        return parseDateOnly(val);
      };

      const fmtCellDate = (d: Date | null): string => (d ? formatDate(d, 'yyyy-MM-dd') : '');

      const monthTicks: Array<{ left: number; label: string }> = [];
      {
        const startDate = new Date(rangeStartMs);
        const tick = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        if (tick.getTime() < rangeStartMs) tick.setMonth(tick.getMonth() + 1, 1);
        while (tick.getTime() <= rangeEndMs) {
          const left = ((tick.getTime() - rangeStartMs) / totalMs) * 100;
          monthTicks.push({ left, label: formatDate(tick, 'MMM yyyy') });
          tick.setMonth(tick.getMonth() + 1, 1);
        }
      }

      const rowsHtml = childTasks
        .map((t, idx) => {
          const api = t.sourceTaskId != null ? apiTaskById.get(t.sourceTaskId) : undefined;
          const foremanName = api?.foremanArea?.name ?? '–';
          const taskTitle = [api?.taskNumber, stripScheduleIconPrefix(t.text)].filter(Boolean).join(' – ');
          const startDate = toDate(t.plannedStartDate) ?? startOfDay(t.start);
          const endDate = toDate(t.plannedCompletionDate) ?? startOfDay(t.end ?? t.start);
          const startMs = Math.max(startDate.getTime(), rangeStartMs);
          const endMsRaw = Math.min(endDate.getTime(), rangeEndMs);
          const endMs = Math.max(startMs + 24 * 60 * 60 * 1000, endMsRaw);
          const leftPct = ((startMs - rangeStartMs) / totalMs) * 100;
          const widthPct = Math.max(0.8, ((endMs - startMs) / totalMs) * 100);
          const notesText = formatDependencyNotes(t.dependencies, sourceTaskIdToLabel);

          return `
            <tr>
              <td class="num">${idx + 1}</td>
              <td>${escapeHtml(foremanName)}</td>
              <td class="task-name">${escapeHtml(t.project ?? '–')}<div class="sub">${escapeHtml(taskTitle)}</div></td>
              <td class="date">${escapeHtml(fmtCellDate(startDate))}</td>
              <td class="date">${escapeHtml(fmtCellDate(endDate))}</td>
              <td class="qty">–</td>
              <td class="uom">–</td>
              <td class="notes">${escapeHtml(notesText)}</td>
              <td class="timeline-cell" data-print-node-id="${t.id}">
                <div class="timeline-track">
                  <div class="timeline-bar-blue print-bar-end print-bar-start" style="left:${leftPct}%;width:${widthPct}%"></div>
                  <div class="timeline-bar-overlay print-bar-end print-bar-start" style="left:${leftPct}%;width:${widthPct}%;background:${barColor(t.type)}"></div>
                  <div class="timeline-label" style="left:${leftPct}%">${escapeHtml(fmtCellDate(startDate))} - ${escapeHtml(fmtCellDate(endDate))}</div>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');

      const ticksHtml = monthTicks
        .map((tick) => `<div class="tick" style="left:${tick.left}%"><span>${escapeHtml(tick.label)}</span></div>`)
        .join('');

      const scheduleTitle = 'Dawson Road Maintenance';
      const subtitle = 'Quantified Plan Schedule – Task Schedule';
      /** Multiselect values print as a comma-separated list; empty selections are omitted. */
      const filterList = (label: string, values: Array<string | number> | undefined | null) => {
        const normalized = normalizeScheduleFilterValues(values);
        return normalized.length > 0 ? `${label}: ${normalized.join(', ')}` : null;
      };
      const filtersSummary = [
        filterList('Service Area', filters.serviceAreas),
        filterList('Foreman Area', filters.foremanAreas),
        filterList('Equipment Type', filters.equipmentTypes),
        filterList('Project', filters.projectIds),
        filterList('Status', filters.statuses),
        filters.startDate ? `Start Date: ${filters.startDate}` : null,
        filters.endDate ? `End Date: ${filters.endDate}` : null,
        filterList('Work Type', filters.workTypes),
        filters.taskSearch ? `Search: ${filters.taskSearch}` : null,
      ]
        .filter(Boolean)
        .join(' | ');

      const arrowScript = buildPrintDependencyArrowInitScript(printLinks);

      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(subtitle)}</title>
            <style>
              @page { size: A4 landscape; margin: 8mm; }
              * { box-sizing: border-box; }
              body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #111; position: relative; }
              .title { text-align: center; font-size: 12px; font-weight: 700; margin-top: 2px; }
              .subtitle { text-align: center; font-size: 10px; margin-top: 2px; margin-bottom: 6px; }
              .meta { font-size: 9px; margin-bottom: 4px; color: #333; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8px; }
              th, td { border: 1px solid #a9a9a9; padding: 2px 3px; vertical-align: middle; }
              th { background: #efefef; font-weight: 700; }
              .num { width: 2%; text-align: center; }
              .fa { width: 8%; }
              .task { width: 28%; }
              .date { width: 6%; white-space: nowrap; }
              .qty { width: 4%; text-align: right; }
              .uom { width: 4%; text-align: center; }
              .notes { width: 14%; }
              .timeline { width: 28%; padding: 0; }
              .task-name { font-weight: 700; }
              .task-name .sub { font-weight: 400; margin-top: 1px; }
              .timeline-head { position: relative; height: 24px; background: #f7f7f7; overflow: hidden; }
              .timeline-head .tick { position: absolute; top: 0; bottom: 0; border-left: 1px solid #d0d0d0; width: 0; }
              .timeline-head .tick span { position: absolute; top: 1px; left: 2px; font-size: 7px; color: #555; white-space: nowrap; }
              .timeline-cell { padding: 0; background: #fafafa; position: relative; }
              .timeline-track { position: relative; height: 14px; }
              .timeline-bar-blue { position: absolute; top: 5px; height: 4px; border-radius: 1px; min-width: 2px; background: #86b8e7; }
              .timeline-bar-overlay { position: absolute; top: 5px; height: 4px; border-radius: 1px; min-width: 2px; opacity: .45; }
              .timeline-label { position: absolute; top: -8px; font-size: 6.5px; color: #2b2b2b; white-space: nowrap; }
              .legend { margin-top: 4px; font-size: 8px; }
              .legend span { display: inline-block; margin-right: 8px; }
              .swatch { display: inline-block; width: 10px; height: 4px; margin-right: 3px; vertical-align: middle; }
            </style>
          </head>
          <body>
            <svg id="schedule-print-arrows" xmlns="http://www.w3.org/2000/svg" style="position:absolute;left:0;top:0;pointer-events:none;z-index:100;overflow:visible"></svg>
            <div class="title">${escapeHtml(scheduleTitle)}</div>
            <div class="subtitle">${escapeHtml(subtitle)}</div>
            <div class="meta">${escapeHtml(filtersSummary || 'All filters')} | ${escapeHtml(`${dateFrom} to ${dateTo}`)}</div>
            <table>
              <thead>
                <tr>
                  <th class="num">#</th>
                  <th class="fa">Foreman Area</th>
                  <th class="task">Task Name</th>
                  <th class="date">Start</th>
                  <th class="date">Finish</th>
                  <th class="qty">QTY</th>
                  <th class="uom">Unit</th>
                  <th class="notes">Notes</th>
                  <th class="timeline">
                    <div class="timeline-head">${ticksHtml}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml || `<tr><td colspan="9">No tasks match current filters.</td></tr>`}
              </tbody>
            </table>
            <div class="legend">
              <span><i class="swatch" style="background:#86b8e7"></i>Planned</span>
              <span><i class="swatch" style="background:#58a55c"></i>Active</span>
              <span><i class="swatch" style="background:#333333"></i>Overdue</span>
            </div>
            <script>${arrowScript}</script>
          </body>
        </html>
      `;

      const win = window.open('', '_blank', 'width=1400,height=900');
      if (!win) {
        setPrintError('The print window was blocked. Allow pop-ups for this site and print again.');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
    } catch (err) {
      console.error('Failed to print task schedule:', err);
      setPrintError("Couldn't print the schedule. Try again.");
    }
  }, [safeTasks, scheduleTasks, rangeStart, rangeEnd, filters, dateFrom, dateTo]);

  useEffect(() => {
    (window as unknown as { __printSchedulePdf?: () => void }).__printSchedulePdf = handlePrintSchedulePdf;
    return () => {
      const w = window as unknown as { __printSchedulePdf?: () => void };
      if (w.__printSchedulePdf === handlePrintSchedulePdf) {
        delete w.__printSchedulePdf;
      }
    };
  }, [handlePrintSchedulePdf]);

  const safeScales = Array.isArray(scalesConfig) ? scalesConfig : [];

  useScheduleGanttChartScroll(viewportRef, bottomHScrollRef, bottomHScrollInnerRef, [
    safeTasks.length,
    loading,
    rangeStart,
    rangeEnd,
    scalePreset,
  ]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const applyPinnedScale = () => {
      const scale = viewport.querySelector('div.wx-scale') as HTMLElement | null;
      if (!scale) return;

      const y = viewport.scrollTop;
      scale.style.transform = `translateY(${y}px)`;
      scale.style.zIndex = '20';
      scale.style.background = '#fff';
      scale.style.boxShadow = '0 1px 0 0 rgba(0, 0, 0, 0.1)';
      scale.style.willChange = 'transform';

      // Pin exactly one left-header container to avoid duplicated stacked headers.
      const leftHeaderContainer =
        (viewport.querySelector('.wx-header-wrapper') as HTMLElement | null) ||
        (viewport.querySelector('.wx-grid-header') as HTMLElement | null) ||
        (viewport.querySelector('.wx-grid thead') as HTMLElement | null);
      if (leftHeaderContainer) {
        leftHeaderContainer.style.transform = `translateY(${y}px)`;
        leftHeaderContainer.style.position = 'relative';
        leftHeaderContainer.style.zIndex = '60';
        leftHeaderContainer.style.background = '#d6e4ff';
        leftHeaderContainer.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.06)';
        leftHeaderContainer.style.overflow = 'visible';
        leftHeaderContainer.style.willChange = 'transform';
      }

      // Ensure nested header rows/cells don't get separately transformed.
      const nestedHeaderRows = viewport.querySelectorAll<HTMLElement>('.wx-header, .wx-h-row');
      nestedHeaderRows.forEach((el) => {
        el.style.transform = '';
        el.style.willChange = '';
      });

      // Today line X: same date range as Gantt start/end (matches task bars). Week labels alone are a shorter span.
      const today = startOfDay(new Date());
      const scaleWidth = Number((scale.style.width || '0px').replace('px', '')) || scale.offsetWidth;
      const rows = scale.querySelectorAll('.wx-row');
      const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
      const cells = lastRow ? (Array.from(lastRow.querySelectorAll('.wx-cell')) as HTMLElement[]) : [];
      const scaleRect = scale.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const scaleLeftInViewport = scaleRect.left - viewportRect.left;
      const todayXInScale = computeTodayMarkerOffsetInScalePx({
        scaleWidth,
        rangeStart,
        rangeEnd,
        scalePreset,
        cells,
        today,
      });

      const lineLeft = scaleLeftInViewport + todayXInScale;
      const lineTop = viewport.scrollTop + scale.offsetHeight;
      const lineHeight = Math.max(0, viewport.scrollHeight - lineTop);

      let line = viewport.querySelector('.schedule-today-fallback-line') as HTMLElement | null;
      if (!line) {
        line = document.createElement('div');
        line.className = 'schedule-today-fallback-line';
        line.setAttribute('role', 'presentation');
        viewport.appendChild(line);
      }
      const todayTip = `Today – ${formatDate(startOfDay(new Date()), 'yyyy-MM-dd')}`;
      line.setAttribute('title', todayTip);
      line.setAttribute('aria-label', todayTip);
      /* 12px hit area centered on previous 2px line (center was lineLeft + 1px) */
      line.style.left = `${lineLeft - 5}px`;
      line.style.top = `${lineTop}px`;
      line.style.height = `${lineHeight}px`;

      viewport.querySelectorAll('.schedule-today-marker').forEach((node) => {
        const el = node as HTMLElement;
        el.setAttribute('title', todayTip);
        el.setAttribute('aria-label', todayTip);
      });
    };

    const run = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(applyPinnedScale);
      });
    };
    run();
    const onScroll = () => applyPinnedScale();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    const hScrollEl = getScheduleGanttHorizontalScrollTarget(viewport);
    if (hScrollEl && hScrollEl !== viewport) {
      hScrollEl.addEventListener('scroll', onScroll, { passive: true });
    }
    const ro = new ResizeObserver(run);
    const scaleEl = viewport.querySelector('div.wx-scale');
    if (scaleEl) ro.observe(scaleEl);
    const timeoutId = window.setTimeout(run, 200);
    return () => {
      viewport.removeEventListener('scroll', onScroll);
      if (hScrollEl && hScrollEl !== viewport) {
        hScrollEl.removeEventListener('scroll', onScroll);
      }
      ro.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [safeTasks.length, loading, rangeStart, rangeEnd, scalePreset]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const decorateTaskNumberLinks = () => {
      const rows = Array.from(viewport.querySelectorAll('.wx-grid .wx-row')) as HTMLElement[];
      rows.forEach((row) => {
        const rowIdRaw = row.getAttribute('data-id') || '';
        const syntheticId = Number.parseInt(rowIdRaw, 10);
        if (!Number.isFinite(syntheticId)) return;

        const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
        const sourceTaskId = svarTask?.sourceTaskId;
        if (!sourceTaskId) return;

        const cells = Array.from(row.querySelectorAll('.wx-cell')) as HTMLElement[];
        if (cells.length < 2) return;

        const taskNumberCell = cells[1];
        const taskNumberText = (taskNumberCell.textContent || '').trim();
        if (!taskNumberText || taskNumberCell.querySelector('a[data-task-link="true"]')) return;

        const taskUrl = `/tasks/quantified?taskId=${sourceTaskId}`;
        taskNumberCell.textContent = '';
        taskNumberCell.style.whiteSpace = 'nowrap';

        const link = document.createElement('a');
        link.href = taskUrl;
        link.dataset.taskLink = 'true';
        link.textContent = taskNumberText;
        // V3-034: the shared ID-link style (weight 500, primary blue, underline on hover only) at
        // the size Tasks and Projects use, not an always-underlined 14px link.
        link.className = `${ID_LINK_CLASS} tw-text-base`;
        link.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openTaskInNewTab(sourceTaskId);
        });

        taskNumberCell.appendChild(link);
      });
    };

    decorateTaskNumberLinks();
    const timerId = window.setTimeout(decorateTaskNumberLinks, 150);
    const observer = new MutationObserver(decorateTaskNumberLinks);
    observer.observe(viewport, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(timerId);
      observer.disconnect();
    };
  }, [safeTasks, openTaskInNewTab]);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-grow tw-text-content-muted">
        Loading task schedule...
      </div>
    );
  }

  return (
    <div className="schedule-gantt-wrapper tw-grow tw-flex tw-flex-col min-h-0 tw-border tw-border-line tw-rounded tw-bg-surface-alt tw-relative">
      {printError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setPrintError(null)} className="tw-m-2 tw-shrink-0">
          {printError}
        </Alert>
      )}
      <div className="schedule-gantt-scroll-shell">
        <div className="schedule-gantt-viewport schedule-gantt-viewport--hscroll-mirror" ref={viewportRef}>
          {safeTasks.length === 0 ? (
            <div className="tw-p-4 tw-text-content-muted">No tasks match the current filters.</div>
          ) : (
            <Willow>
              <Tooltip api={ganttApi as import('@svar-ui/react-gantt').IApi | undefined} content={tooltipContent}>
                <Gantt
                  init={initGantt}
                  tasks={safeTasks}
                  links={safeLinks}
                  scales={safeScales}
                  columns={columns}
                  taskTypes={taskTypes}
                  start={rangeStart}
                  end={rangeEnd}
                  autoScale={false}
                  cellWidth={SCHEDULE_GANTT_TIMELINE_CELL_WIDTH}
                  {...({ markers: todayMarker } as any)}
                />
              </Tooltip>
            </Willow>
          )}
        </div>
      </div>
      <div
        className="schedule-gantt-hscroll"
        ref={bottomHScrollRef}
        aria-label="Scroll dates horizontally"
        title="Scroll dates horizontally"
      >
        <div ref={bottomHScrollInnerRef} className="schedule-gantt-hscroll-inner" />
      </div>
      <div className="tw-border-t tw-border-line tw-bg-surface-alt tw-px-2 tw-py-1 tw-text-sm tw-text-content-muted tw-flex tw-justify-between tw-items-center tw-flex-wrap tw-gap-2">
        <span>
          {scalePreset[0].toUpperCase() + scalePreset.slice(1)} view from {formatDate(rangeStart, 'yyyy-MM-dd')} to{' '}
          {formatDate(rangeEnd, 'yyyy-MM-dd')}. Expand a project, then a task row, to view assigned resources.
        </span>
      </div>
    </div>
  );
};

export default TaskGanttChart;
